import classNames from "classnames"
import Color from "color"
import { DetailedHTMLProps, HTMLAttributes, useState } from "react"

type DivElementType = DetailedHTMLProps<
  HTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

type ButtonSize = "sm" | "base" | "lg"

export const MainButton: React.FC<
  {
    children: React.ReactNode
    disabled?: boolean
    baseColor?: string
    size?: ButtonSize
  } & DivElementType
> = ({ children, baseColor, size = "lg", ...rest }) => {
  const [isPressed, setIsPressed] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const color = new Color(baseColor || "#7b2d26")
  const mainColor = color.hex()
  const shadowColor = color.darken(0.5).fade(0.7).string()
  const shadowColor2 = color.darken(0.3).fade(0.5).string()
  const shadowColor3 = color.darken(0.15).fade(0.3).string()
  const highlightColor = color.lighten(0.8).fade(0.85).string()
  const borderColor = color.lighten(0.2).fade(0.85).string()

  const sizeClasses = {
    sm: "h-10 min-w-[56px] px-5 text-base",
    base: "h-14 min-w-[80px] sm:h-12 sm:min-w-[60px] px-7 sm:px-4 text-lg",
    lg: "h-16 min-w-[96px] sm:min-w-[60px] sm:px-2 px-8 text-xl sm:text-3xl",
  }

  const isDisabled = rest.disabled

  return (
    <button
      {...rest}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsPressed(false)
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      className={classNames(
        "relative inline-flex items-center justify-center font-poppins rounded-[1.5rem] sm:rounded-[0.875rem] text-white font-semibold select-none overflow-hidden border focus:outline-none transition-all duration-150 ease-in-out",
        sizeClasses[size],
        {
          "opacity-50 cursor-not-allowed": isDisabled,
          "scale-105": isHovered && !isDisabled,
          "scale-95": isPressed && !isDisabled,
        },
        rest.className
      )}
      style={{
        backgroundColor: mainColor,
        boxShadow: isDisabled
          ? "none"
          : [
              `0 10px 32px 0 ${shadowColor}`,
              `0 4px 16px 0 ${shadowColor2}`,
              `0 1.5px 4px 0 ${shadowColor3}`,
              `0 0.5px 0.5px 0 ${highlightColor} inset`,
              `0 -2px 8px 0 ${highlightColor} inset`,
            ].join(", "),
        borderColor: borderColor,
        borderWidth: "1.5px",
        borderStyle: "solid",
        outline: "none",
      }}
    >
      {/* Faint highlight overlay on hover */}
      {isHovered && !isDisabled && (
        <div
          className="absolute inset-0 pointer-events-none rounded-[1.5rem] sm:rounded-[0.875rem]"
          style={{
            background:
              "linear-gradient(0deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)",
          }}
        />
      )}
      <span className="relative z-10 flex items-center justify-center w-full h-full ">
        {children}
      </span>
    </button>
  )
}
